import React, { useEffect, useRef, useState } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Badge, Drawer, Result, Spin, Grid, Tooltip, message } from 'antd'
import {
  DashboardOutlined,
  ShoppingOutlined,
  ContactsOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  FileOutlined,
  BarChartOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  MenuOutlined,
  ShopOutlined,
  ApiOutlined,
  AppstoreOutlined,
  TagOutlined,
  BankOutlined,
  ImportOutlined,
  CloudOutlined,
  ToolOutlined,
  LinkOutlined,
  AuditOutlined,
  DatabaseOutlined,
  RollbackOutlined,
  SafetyCertificateOutlined,
  InboxOutlined,
  WalletOutlined,
  PushpinOutlined,
  PushpinFilled
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import ProfileModal from '../Modals/ProfileModal'
import ChangePasswordModal from '../Modals/ChangePasswordModal'
import api from '../../utils/api'

const { Header, Sider, Content } = Layout
const { useBreakpoint } = Grid
const DESKTOP_SIDER_WIDTH = 260
const COLLAPSED_SIDER_WIDTH = 80
const SUPER_ADMIN_PROTECTED_MENU_PATHS = ['/settings/access-control']

const DashboardLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false)
  const [profileModalVisible, setProfileModalVisible] = useState(false)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)
  const [pinnedKeys, setPinnedKeys] = useState([])
  const [pinsLoaded, setPinsLoaded] = useState(false)
  const pinSavingRef = useRef(false)

  const { user, logout, hasPermission, hasRole, accessMenus, accessLoading, canAccessPath } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const screens = useBreakpoint()
  const isMobile = screens.md === false

  useEffect(() => {
    let active = true
    setPinnedKeys([])
    setPinsLoaded(false)
    if (user?.id) {
      api.get('/api/access-control/my-pins')
        .then(response => {
          if (active) setPinnedKeys(Array.isArray(response.data?.data) ? response.data.data : [])
        })
        .catch(() => { if (active) message.error('โหลดเมนูที่ปักหมุดไม่สำเร็จ') })
        .finally(() => { if (active) setPinsLoaded(true) })
    }
    return () => { active = false }
  }, [user?.id])

  const togglePin = async key => {
    if (!pinsLoaded || pinSavingRef.current) return
    const next = pinnedKeys.includes(key) ? pinnedKeys.filter(item => item !== key) : [...pinnedKeys, key]
    if (next.length > 40) return message.warning('ปักหมุดได้สูงสุด 40 เมนู')
    pinSavingRef.current = true
    setPinnedKeys(next)
    try {
      await api.put('/api/access-control/my-pins', { menu_keys: next })
    } catch (error) {
      setPinnedKeys(pinnedKeys)
      message.error('บันทึกการปักหมุดไม่สำเร็จ')
    } finally {
      pinSavingRef.current = false
    }
  }

  // Menu items based on user permissions
  const getMenuItems = () => {
    const items = [{ key: '/dashboard', icon: <DashboardOutlined />, label: 'แดชบอร์ด' }]

    if (hasPermission('expense_import', 'read')) {
      items.push({ key: '/expenses/import-review', icon: <AuditOutlined />, label: 'รอตรวจสอบ' })
    }

    const salesChildren = []
    if (hasPermission('print_estimates', 'read')) salesChildren.push({ key: '/print-estimates', icon: <FileTextOutlined />, label: 'ใบรับงาน / ประเมินราคา' })
    if (hasPermission('contacts', 'read')) salesChildren.push({ key: '/contacts', icon: <ContactsOutlined />, label: 'ลูกค้าและคู่ค้า' })
    if (hasPermission('quotes', 'read')) {
      salesChildren.push(
        { key: '/quotes', icon: <FileTextOutlined />, label: 'ใบเสนอราคา' },
        { key: '/delivery-notes', icon: <FileTextOutlined />, label: 'ใบส่งของชั่วคราว' },
        { key: '/delivery-pending', icon: <FileTextOutlined />, label: 'ใบค้างส่ง' }
      )
    }
    if (hasPermission('receipts', 'read')) salesChildren.push({ key: '/receipts', icon: <FileTextOutlined />, label: 'แจ้งหนี้ / ใบเสร็จ' })
    if (hasPermission('sales_returns', 'read')) salesChildren.push({ key: '/sales-returns', icon: <RollbackOutlined />, label: 'รับคืนสินค้า' })
    if (hasPermission('warranty', 'read') || hasPermission('rma', 'read')) {
      salesChildren.push({ key: '/warranties', icon: <SafetyCertificateOutlined />, label: 'รับประกันและ RMA' })
    }
    if (salesChildren.length) items.push({ key: 'sales-work', icon: <ShopOutlined />, label: 'งานขายและลูกค้า', children: salesChildren })

    const inventoryChildren = []
    if (hasPermission('products', 'read')) inventoryChildren.push({ key: '/products', icon: <ShoppingOutlined />, label: 'สินค้าและ Variant' })
    if (hasPermission('inventory', 'read')) inventoryChildren.push({ key: '/inventory', icon: <DatabaseOutlined />, label: 'คลังสินค้าและ Serial' })
    if (hasPermission('purchases', 'read')) {
      inventoryChildren.push(
        { key: '/materials', icon: <ToolOutlined />, label: 'วัสดุงานป้าย' },
        { key: '/purchases', icon: <FileTextOutlined />, label: 'ใบสั่งซื้อ' },
        { key: '/purchases/goods-receipts', icon: <InboxOutlined />, label: 'ใบรับสินค้าเข้าคลัง' }
      )
    }
    if (inventoryChildren.length) items.push({ key: 'purchase-inventory', icon: <DatabaseOutlined />, label: 'สินค้า จัดซื้อ และคลัง', children: inventoryChildren })

    const financeChildren = []
    if (hasPermission('expenses', 'read')) financeChildren.push({ key: '/expenses', icon: <CreditCardOutlined />, label: 'ค่าใช้จ่าย' })
    financeChildren.push(
      { key: '/director-advance', icon: <BankOutlined />, label: 'เงินทดรองกรรมการ' },
      { key: '/bank-statement-import', icon: <ImportOutlined />, label: 'รายการ Statement' }
    )
    items.push({ key: 'finance-work', icon: <CreditCardOutlined />, label: 'การเงินและค่าใช้จ่าย', children: financeChildren })

    items.push({
      key: 'accounting-work',
      icon: <BarChartOutlined />,
      label: 'ระบบบัญชี',
      children: [
        { key: '/accounting/chart-of-accounts', label: 'ผังบัญชี' },
        { key: '/accounting/bank-accounts', label: 'บัญชีธนาคารบริษัท' },
        { key: '/accounting/journal-entries', label: 'สมุดรายวันทั่วไป' },
        { key: '/accounting/transfers', label: 'โอนเงินระหว่างบัญชี' },
        { key: '/accounting/deposits', label: 'ฝากเงินเข้าบัญชี' },
        { key: '/accounting/settings', label: 'ตั้งค่าการลงบัญชี' },
        ...(hasPermission('accounting', 'post_accounting') ? [{ key: '/accounting/bulk-posting', label: 'ลงบัญชี One Click' }] : []),
        { key: '/accounting/reports', label: 'รายงานทางการเงิน' },
        ...(hasRole('SUPER_ADMIN') ? [{ key: '/accounting/danger-zone', label: 'ล้างสมุดรายวัน' }] : [])
      ]
    })

    const workChildren = []
    if (hasPermission('projects', 'read')) workChildren.push({ key: '/projects', icon: <ContactsOutlined />, label: 'โปรเจ็ค' })
    if (hasPermission('calendar', 'read')) workChildren.push({ key: '/calendar', icon: <CalendarOutlined />, label: 'ปฏิทิน' })
    if (hasPermission('notes', 'read')) workChildren.push({ key: '/notes', icon: <FileOutlined />, label: 'บันทึก' })
    if (workChildren.length) items.push({ key: 'internal-work', icon: <AppstoreOutlined />, label: 'งานและการวางแผน', children: workChildren })

    if (hasPermission('reports', 'read')) items.push({ key: '/reports', icon: <BarChartOutlined />, label: 'รายงานภาพรวม' })
    if (hasRole('INVESTOR')) items.push({ key: '/investor-dashboard', icon: <WalletOutlined />, label: 'ส่วนแบ่งของฉัน' })
    if (hasPermission('investor_management', 'manage')) items.push({ key: '/investors', icon: <TeamOutlined />, label: 'จัดการนักลงทุน' })

    // Settings menu
    if (hasPermission('settings', 'read') || hasPermission('ai_settings', 'read') || hasPermission('line_integration', 'read')) {
      items.push({
        key: 'system-settings',
        icon: <SettingOutlined />,
        label: 'การตั้งค่า',
        children: [
          {
            key: '/settings/company',
            icon: <ShopOutlined />,
            label: 'ข้อมูลบริษัท',
          },
          ...((hasRole('SUPER_ADMIN') || hasRole('ADMIN') || hasPermission('users', 'read')) ? [{
            key: '/settings/access-control?tab=users',
            icon: <TeamOutlined />,
            label: 'ผู้ใช้งานและสิทธิ์',
          }] : []),
          {
            key: '/settings/api',
            icon: <ApiOutlined />,
            label: 'การตั้งค่า API',
          },
          ...(hasRole('SUPER_ADMIN') ? [{
            key: '/settings/storage',
            icon: <CloudOutlined />,
            label: 'พื้นที่จัดเก็บเอกสาร',
          }] : []),
          {
            key: '/settings/api/docs',
            icon: <FileTextOutlined />,
            label: 'API Documentation',
          },
          {
            key: '/settings/units',
            icon: <AppstoreOutlined />,
            label: 'หน่วยนับ',
          },
          {
            key: '/settings/product-categories',
            icon: <TagOutlined />,
            label: 'หมวดหมู่สินค้า',
          },
          ...(hasPermission('ai_settings', 'read') ? [{
            key: '/settings/ai', icon: <ApiOutlined />, label: 'ตั้งค่า AI / LLM',
          }] : []),
          ...(hasPermission('line_integration', 'read') ? [{
            key: '/settings/integrations/line', icon: <LinkOutlined />, label: 'เชื่อมต่อ LINE OA',
          }] : [])
        ]
      })

    }


    // OCR Upload is available to all users
    // items.push({
    //   key: '/upload',
    //   icon: <CloudUploadOutlined />,
    //   label: 'อัปโหลดเอกสาร',
    // })

    const filterItems = source => source.map(item => {
      if (item.children) {
        const children = filterItems(item.children)
        return children.length ? { ...item, children } : null
      }
      const itemPath = typeof item.key === 'string' ? item.key.split('?')[0] : item.key
      if (hasRole('SUPER_ADMIN') && SUPER_ADMIN_PROTECTED_MENU_PATHS.includes(itemPath)) return item
      const config = accessMenus.find(menu => menu.path === itemPath)
      if (!config) return item
      if (!config.is_enabled) return null
      if (config.resource_key && !hasPermission(config.resource_key, 'read')) return null
      return item
    }).filter(Boolean)
    return filterItems(items)
  }

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'โปรไฟล์',
      onClick: () => setProfileModalVisible(true)
    },
    {
      key: 'change-password',
      icon: <SettingOutlined />,
      label: 'เปลี่ยนรหัสผ่าน',
      onClick: () => setPasswordModalVisible(true)
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ออกจากระบบ',
      onClick: logout
    }
  ]

  const handleMenuClick = (e) => {
    navigate(e.key)
    setMobileDrawerVisible(false)
  }

  const currentPath = location.pathname
  const visibleMenuItems = getMenuItems()
  const visibleLeaves = []
  const collectLeaves = (items, parentIcon = null) => items.forEach(item => {
    if (item.children) collectLeaves(item.children, item.icon || parentIcon)
    else visibleLeaves.push({ ...item, icon: item.icon || parentIcon || <FileOutlined /> })
  })
  collectLeaves(visibleMenuItems)
  const leafByKey = new Map(visibleLeaves.map(item => [item.key, item]))
  const visiblePinnedKeys = pinnedKeys.filter(key => leafByKey.has(key))
  const pinnedSet = new Set(visiblePinnedKeys)
  const pinLabel = item => (
    <span className="sidebar-menu-label">
      <span className="sidebar-menu-text">{item.label}</span>
      <Tooltip title={pinnedSet.has(item.key) ? 'ถอนหมุด' : 'ปักหมุดไว้ด้านบน'} placement="right">
        <button type="button" className={`sidebar-pin-button${pinnedSet.has(item.key) ? ' is-pinned' : ''}`}
          aria-label={`${pinnedSet.has(item.key) ? 'ถอนหมุด' : 'ปักหมุด'} ${item.label}`}
          disabled={!pinsLoaded}
          onClick={event => { event.preventDefault(); event.stopPropagation(); togglePin(item.key) }}
          onMouseDown={event => event.stopPropagation()}>
          {pinnedSet.has(item.key) ? <PushpinFilled /> : <PushpinOutlined />}
        </button>
      </Tooltip>
    </span>
  )
  const remainingItems = items => items.map(item => {
    if (item.children) {
      const children = remainingItems(item.children)
      return children.length ? { ...item, children } : null
    }
    return pinnedSet.has(item.key) ? null : { ...item, title: item.label, label: pinLabel(item) }
  }).filter(Boolean)
  const menuItems = [
    ...visiblePinnedKeys.map(key => {
      const item = leafByKey.get(key)
      return { ...item, title: item.label, label: pinLabel(item) }
    }),
    ...(visiblePinnedKeys.length ? [{ type: 'divider', key: 'pinned-divider' }] : []),
    ...remainingItems(visibleMenuItems)
  ]
  const collectMenuKeys = items => items.flatMap(item => [
    ...(typeof item?.key === 'string' && item.key.startsWith('/') ? [{ key: item.key, path: item.key.split('?')[0] }] : []),
    ...(item?.children ? collectMenuKeys(item.children) : [])
  ])
  const activeMenuKey = collectMenuKeys(menuItems)
    .filter(item => currentPath === item.path || currentPath.startsWith(`${item.path}/`))
    .sort((left, right) => right.path.length - left.path.length)[0]?.key
  const selectedKeys = activeMenuKey ? [activeMenuKey] : []
  const findParentKeys = (items, targetKey, parents = []) => {
    for (const item of items) {
      if (item.key === targetKey) return parents
      if (item.children) {
        const found = findParentKeys(item.children, targetKey, [...parents, item.key])
        if (found) return found
      }
    }
    return null
  }
  const activeParentKeys = findParentKeys(menuItems, activeMenuKey) || []
  const [openMenuKeys, setOpenMenuKeys] = useState(activeParentKeys)
  const activeParentSignature = activeParentKeys.join('|')
  useEffect(() => {
    if (!activeParentKeys.length) return
    setOpenMenuKeys(activeParentKeys)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenuKey, activeParentSignature])
  const rootSubmenuKeys = menuItems.filter(item => item.children?.length).map(item => item.key)
  const handleOpenMenuChange = keys => {
    const latestKey = keys.find(key => !openMenuKeys.includes(key))
    setOpenMenuKeys(latestKey && rootSubmenuKeys.includes(latestKey) ? [latestKey] : keys)
  }
  const bottomItems = [
    { key: '/dashboard', label: 'หน้าหลัก', icon: <DashboardOutlined /> },
    ...(hasPermission('expense_import', 'read') ? [{ key: '/expenses/import-review', label: 'รอตรวจ', icon: <AuditOutlined /> }] : []),
    ...(hasPermission('receipts', 'read') ? [{ key: '/receipts', label: 'รายรับ', icon: <FileTextOutlined /> }] : []),
    ...(hasPermission('expenses', 'read') ? [{ key: '/expenses', label: 'รายจ่าย', icon: <CreditCardOutlined /> }] : []),
    ...(hasPermission('accounting', 'read') || hasPermission('reports', 'read') ? [{ key: '/accounting/reports', label: 'งบการเงิน', icon: <BarChartOutlined /> }] : [])
  ].slice(0, 5)
  const activeBottomKey = bottomItems
    .filter(item => currentPath === item.key || currentPath.startsWith(`${item.key}/`))
    .sort((a, b) => b.key.length - a.key.length)[0]?.key

  const siderContent = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={selectedKeys}
      openKeys={openMenuKeys}
      onOpenChange={handleOpenMenuChange}
      items={menuItems}
      onClick={handleMenuClick}
      className="organized-sidebar-menu"
      style={{ height: '100%', borderRight: 0, paddingBottom: 20 }}
    />
  )

  return (
    <Layout className="app-layout">
      {/* Desktop Sidebar */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={DESKTOP_SIDER_WIDTH}
        collapsedWidth={COLLAPSED_SIDER_WIDTH}
        className="hidden-mobile"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="app-logo" style={{
          height: 64,
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: 'white',
          fontSize: collapsed ? '16px' : '18px',
          fontWeight: 600,
          borderBottom: '1px solid #303030'
        }}>
          {collapsed ? 'บจ' : 'ระบบจัดการธุรกิจ'}
        </div>
        {siderContent}
      </Sider>

      {/* Mobile Drawer */}
      <Drawer
        title={
          <div className="mobile-app-menu-title">
            <span className="mobile-app-menu-logo"><AppstoreOutlined /></span>
            <span><strong>ATOMSOFT ERP</strong><small>เมนูหลัก</small></span>
          </div>
        }
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        width="100%"
        rootClassName="mobile-app-menu-drawer-root"
        className="visible-mobile mobile-app-menu-drawer"
        styles={{ body: { padding: 0 }, header: { padding: '14px 16px' } }}
      >
        <div className="mobile-app-menu-body">{siderContent}</div>
      </Drawer>

      <Layout style={{
        marginLeft: isMobile ? 0 : (collapsed ? COLLAPSED_SIDER_WIDTH : DESKTOP_SIDER_WIDTH),
        transition: 'margin-left 0.2s ease'
      }}>
        {/* Header */}
        <Header className="app-header" style={{
          position: 'fixed',
          zIndex: 1000,
          width: isMobile ? '100%' : `calc(100% - ${collapsed ? COLLAPSED_SIDER_WIDTH : DESKTOP_SIDER_WIDTH}px)`,
          padding: isMobile ? '0 12px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'width 0.2s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Mobile menu button */}
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileDrawerVisible(true)}
              className="visible-mobile"
              style={{ color: 'white', marginRight: 16 }}
            />

            {/* Desktop collapse button */}
            <Button
              type="text"
              icon={collapsed ? <MenuOutlined /> : <MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="hidden-mobile"
              style={{ color: 'white' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Notifications */}
            <Badge count={0} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                style={{ color: 'white' }}
              />
            </Badge>

            {/* User menu */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background-color 0.3s'
              }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ marginRight: 8 }}
                />
                <span style={{ color: 'white', display: isMobile ? 'none' : 'block' }}>
                  {user?.name}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Content */}
        <Content
          className="app-content"
          style={{
            marginTop: 64,
            minHeight: 'calc(100vh - 64px)',
            overflow: 'auto',
            paddingBottom: isMobile ? 'calc(84px + env(safe-area-inset-bottom))' : undefined
          }}
        >
          {accessLoading ? <Spin size="large" /> : canAccessPath(location.pathname) ? children : (
            <Result status="403" title="403" subTitle="คุณไม่มีสิทธิ์เข้าถึงหน้านี้ หรือเมนูนี้ถูกปิดใช้งาน"
              extra={<Button type="primary" onClick={() => navigate(hasRole('SUPER_ADMIN') ? '/settings/access-control?tab=menus' : '/dashboard')}>
                {hasRole('SUPER_ADMIN') ? 'ไปตั้งค่าเมนู' : 'กลับแดชบอร์ด'}
              </Button>} />
          )}
        </Content>
      </Layout>

      {isMobile && <nav className="mobile-bottom-nav" aria-label="เมนูหลักบนมือถือ" style={{ gridTemplateColumns: `repeat(${Math.max(bottomItems.length, 1)}, minmax(0, 1fr))` }}>
        {bottomItems.map(item => <button key={item.key} type="button"
          className={`mobile-bottom-nav-item ${activeBottomKey === item.key ? 'active' : ''}`}
          onClick={() => navigate(item.key)} aria-current={activeBottomKey === item.key ? 'page' : undefined}>
          <span className="mobile-bottom-nav-icon">{item.icon}</span>
          <span>{item.label}</span>
        </button>)}
      </nav>}

      {/* Modals */}
      <ProfileModal
        visible={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
      />

      <ChangePasswordModal
        visible={passwordModalVisible}
        onClose={() => setPasswordModalVisible(false)}
      />

      {/* Custom styles for responsive design */}
      <style jsx global>{`
        @media (max-width: 768px) {
          .hidden-mobile {
            display: none !important;
          }
          .visible-mobile {
            display: block !important;
          }
          .app-header { height: 56px; line-height: 56px; }
          .app-content { margin-top: 56px !important; min-height: calc(100dvh - 56px) !important; }
        }
        
        @media (min-width: 769px) {
          .visible-mobile {
            display: none !important;
          }
          .hidden-mobile {
            display: block !important;
          }
        }
      `}</style>
    </Layout>
  )
}

export default DashboardLayout
